import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Col, Grid, Row } from 'react-styled-flexboxgrid';
import styled from 'styled-components';
import { format } from 'date-fns';
import { enUS, pt } from 'date-fns/locale';

import useFollowIncident from './hooks/useFollowIncident';
import useGetImage from './hooks/useGetImage';
import ReopenModal from './ReopenModal';
import { LocationState } from './types';

import { Footer } from '../../App/Footer';
import { Avatar } from '../../Global/Atomics/Avatar';
import Box from '../../Global/Atomics/Box';
import { Button, StyledButton } from '../../Global/Atomics/Button';
import { Container } from '../../Global/Atomics/Container';
import { Heading } from '../../Global/Atomics/Heading';
import LoadingNova from '../../Global/Atomics/LoadingNova';
import NovaCircle from '../../Global/Atomics/NovaCircle';
import { Paragraph } from '../../Global/Atomics/Paragraph';
import CustomIcon from '../../Global/Icons/CustomIcon';
import FeedbackPage from '../../Global/Widgets/Feedbacks/FeedbackPage';
import { Map } from '../../Global/Widgets/Map/Map';

import { useAuthContext } from '../../../contexts/AuthContext';
import { useResponsiveContext } from '../../../contexts/ResponsiveContext';
import { useTranslationContext } from '../../../contexts/TranslationContext';
import lockScrollbar from '../../../utils/helpers/lockScrollbar';
import { statusMapping } from '../../../utils/helpers/statusMapping';

const Image = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 12px;
`;

const Detail = () => {
    const [showModal, setShowModal] = useState(false);
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const navigate = useNavigate();
    const { user } = useAuthContext();
    const [viewMode] = useResponsiveContext();
    const { resources, translate, language } = useTranslationContext();
    const { state } = useLocation() as LocationState;
    const { data: image, isLoading: imgLoading } = useGetImage(); // API call to get incident image;
    const { isLoading, isError, isSuccess, refetch } = useFollowIncident({ setShowFeedbackModal }); // API call to follow incident

    const status = statusMapping(state?.statusLevel);
    const isMobile = viewMode === 'mobile';
    let mapHeight = isMobile ? '260px' : '560px';
    let size = isMobile ? 158 : 220;

    useEffect(() => {
        if (!state) {
            navigate('/home')
        }
    }, [navigate, state]);

    if (!state) return null;

    if (showFeedbackModal || isLoading) {
        return (
            <FeedbackPage
                isError={isError}
                isSuccess={isSuccess}
                isLoading={isLoading}
                successTitle={resources.feedback.successTitle}
                errorDescription={resources.feedback.report.errorDescription2}
                onErrorCTA={() => { setShowFeedbackModal(false); lockScrollbar(false) }}
            />
        )
    }

    const date = state?.date ? format(new Date(state.date), 'dd/LL/u', { locale: language === 'PT' ? pt : enUS }) : null;
    const dateHour = state?.date ? format(new Date(state.date), 'p', { locale: language === 'PT' ? pt : enUS }) : null;

    // CTA Follow
    // Display if not opened_by user and isn't on users watch_list
    const displayCTAFollow = !(user?.sys_id === state?.openedBy) && !(user?.sys_id === state?.watchListId) && !(user?.sys_id === state?.callerId);

    // CTA Reopen
    // Display only if incident has state '6' and was opened_by user or is on users watch_list
    const displayCTAReopen = [6].includes(state?.statusLevel) && (user?.sys_id === state?.openedBy || user?.sys_id === state?.watchListId || user?.sys_id === state?.callerId);

    // My Incident Label
    // Display label "Nos meus incidentes" when the incident was created by the user or it's in his watch list
    const displayLabelMyIncident = (user?.sys_id === state?.openedBy) || (user?.sys_id === state?.watchListId) && !(user?.sys_id === state?.callerId);

    return (
        <>
            <Box position="relative" bg="green-nova" height={displayLabelMyIncident ? "55vh" : "50vh"} pt="lg">
                <Container>
                    <NovaCircle size={size} customPosition="top-right" zIndex={1} />
                    <Box display="flex" justifyContent="end" alignItems="center">
                        <StyledButton onClick={() => navigate(-1)} aria-label="Close" >
                            <CustomIcon icon={"ActionClose_sm"} customColor="white" />
                        </StyledButton>
                    </Box>

                    {/* WELCOME & INFO */}
                    <Row center="md">
                        <Col xs={12} md={9}>
                            <Box ml={isMobile ? "sm" : undefined} pl={isMobile ? "xs" : undefined}>
                                {/* Icon */}
                                <Box mb="lg" display="flex">
                                    {state.icon ? (
                                        <CustomIcon icon={state.icon} customColor="white" width={92} height={60} />
                                    ) : (
                                        <CustomIcon icon="PageNotFound" customColor="white" width={92} height={60} />
                                    )}
                                </Box>

                                {/* Status */}
                                <Box display="flex" alignItems="center" justifyContent="center" width="fit-content" bg="white" borderRadius="xxl" py="sm" px="md" mb="sm">
                                    <Avatar border="none" position="relative" bg={status?.iconColor} size={11} />
                                    <Box as="span" typography="ui-xs-medium" customColor="black-1" ml="xs">{status && (status?.name)}</Box>
                                </Box>

                                {/* My Incident Label */}
                                {displayLabelMyIncident && (
                                    <Box width="fit-content" pt="xs" mt="md" >
                                        <Box typography="ui-xxs-semibold" bg="black-1" customColor="white" py="xs" px="sm" mb="sm">
                                            {resources.label.inMyIncidents}
                                        </Box>
                                    </Box>
                                )}

                                {/* Incident Title */}
                                <Heading level={1} typography="text-l-bold" customColor="white" mb="xs">{state.incidentTitle}</Heading>

                                {/* Section */}
                                <Heading level={2} typography="text-s-bold" customColor="white" mb="sm">Secção {state.section}</Heading>

                                {/* Date */}
                                <Box mb="lg">
                                    <Box as="span" typography="text-xs-regular" mr="md" customColor="white">{date}</Box>
                                    <Box as="span" typography="text-xs-regular" customColor="white">{dateHour}h</Box>
                                </Box>
                            </Box>

                            {/* Incident Description */}
                            <Paragraph typography="text-xs-medium" customColor="white" mb="lg">
                                {state.incidentDescription}
                            </Paragraph>
                        </Col>
                    </Row>

                    {/* INCIDENT IMAGE */}
                    <Row center="xs">
                        <Col xs={8} md={6}>
                            {!imgLoading && image && image?.data ? (
                                <Box position="relative" height={isMobile ? "343px" : "500px"} borderRadius="lg" mb="lg" zIndex={100}>
                                    <Image src={`data:image/jpeg;base64,${image?.data}`} alt={state.incidentTitle} />
                                </Box>
                            ) : (
                                <Box position="relative" height={isMobile ? "343px" : "500px"} bg={'neutral2'} borderRadius="lg" mb="lg" zIndex={100}></Box>
                            )}
                        </Col>
                    </Row>
                </Container>

                {/* MAP */}
                <Container fluid={isMobile}>
                    <Row center="md" style={{ width: '100%', margin: 0 }}>
                        <Col xs={12} md={9} style={{ padding: 0 }}>
                            <Box position="relative" height={mapHeight} zIndex={100} mt="sm" mb="lg" borderRadius={!isMobile ? "lg" : '0'}>
                                <Map
                                    borderRadius={!isMobile ? "12px" : 'none'}
                                    displayMarker
                                    markerPosition={{ lat: state.lat, lng: state.lng }}
                                    center={{ lat: state.lat, lng: state.lng }}
                                />
                            </Box>
                        </Col>
                    </Row>
                </Container>

                <Container>
                    <Row center="xs">
                        <Col xs={9}>
                            {/* CTA FOLLOW*/}
                            {displayCTAFollow && (
                                <Box display="flex" justifyContent="center">
                                    <Button variant='primary-black' size="s" onClick={() => refetch()}>{resources.details.followResolution}</Button>
                                </Box>
                            )}

                            {/* CTA REOPEN */}
                            {displayCTAReopen && (
                                <Box display="flex" flexDirection='column' justifyContent="center" mt="lg">
                                    <Paragraph typography='text-s-medium' mb="md" textAlign='center'>{resources.details.doesIncidentOccur}</Paragraph>
                                    <Box alignSelf="center">
                                        <Button variant='primary-black' size="s" onClick={() => { setShowModal(true) }}>{resources.label.reopenIncident}</Button>
                                    </Box>
                                </Box>
                            )}
                        </Col>
                    </Row>
                </Container>

                {/* MAIN FOOTER */}
                <Footer />
            </Box>

            {/* MODAL MAP REPORT */}
            <ReopenModal showModal={showModal} setShowModal={setShowModal} />

            {/* LOADING */}
            {imgLoading && <LoadingNova />}
        </>
    );
};

export default Detail;
